import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { WorkflowScheduleSummary } from '@/types'
import { Link, useNavigate } from 'react-router-dom'
import { OccurrenceStatusBadge } from './OccurrenceStatusBadge'
import { ScheduleStateBadge } from './ScheduleStateBadge'
import { TargetInactiveBadge } from './TargetInactiveBadge'
import { describeRecurrence, formatInTimeZone, formatRelativeToNow, formatShortInTimeZone } from './scheduleFormat'

interface Props {
  projectId: string
  schedules: WorkflowScheduleSummary[]
}

export function WorkflowSchedulesTable({ projectId, schedules }: Props) {
  const navigate = useNavigate()

  return (
    <div className="rounded-lg border" data-testid="workflow-schedules-table">
      <Table aria-label="Workflow schedules">
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead>Name</TableHead>
            <TableHead className="w-[170px]">Recurrence</TableHead>
            <TableHead className="w-[160px]">Owner</TableHead>
            <TableHead className="w-[120px]">State</TableHead>
            <TableHead className="w-[130px]">Next due</TableHead>
            <TableHead className="w-[150px]">Last outcome</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {schedules.map(s => {
            const detailPath = `/projects/${projectId}/workflows/schedules/${s.id}`
            return (
              <TableRow
                key={s.id}
                className="cursor-pointer"
                onClick={() => navigate(detailPath)}
                data-testid="workflow-schedule-row"
              >
                <TableCell>
                  <div className="flex flex-col">
                    <Link
                      to={detailPath}
                      className="font-medium text-foreground hover:underline"
                      onClick={e => e.stopPropagation()}
                    >
                      {s.name}
                    </Link>
                    <span className="text-xs text-muted-foreground" data-testid="workflow-schedule-target">
                      {s.targetName ?? <span className="italic">Deleted workflow</span>}
                    </span>
                    {s.targetName && !s.targetIsActive && <TargetInactiveBadge className="mt-1 w-fit" />}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className={s.recurrenceKind === 'Cron' ? 'font-mono text-xs' : undefined}>
                      {describeRecurrence(s.recurrenceKind, s.cronExpression, s.intervalMinutes)}
                    </span>
                    <span className="text-xs text-muted-foreground">{s.timeZoneId}</span>
                  </div>
                </TableCell>
                <TableCell>{s.ownerDisplayName ?? <span className="text-muted-foreground">Unknown user</span>}</TableCell>
                <TableCell>
                  <ScheduleStateBadge state={s.state} />
                </TableCell>
                <TableCell>
                  {s.state === 'Paused' ? (
                    <span className="text-muted-foreground">-</span>
                  ) : (
                    <span title={formatInTimeZone(s.nextOccurrenceUtc, s.timeZoneId)}>
                      {formatShortInTimeZone(s.nextOccurrenceUtc, s.timeZoneId)}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {s.lastStatus ? (
                    <div className="flex flex-col items-start gap-1" data-testid="workflow-schedule-last-outcome">
                      <OccurrenceStatusBadge status={s.lastStatus} />
                      <span className="text-xs text-muted-foreground" title={formatInTimeZone(s.lastScheduledForUtc, s.timeZoneId)}>
                        {formatRelativeToNow(s.lastScheduledForUtc)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Never fired</span>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
